import React, { Fragment, ReactNode } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import Head from 'next/head';
import { urlPattern } from 'Routes/routeBuilder';
import { useAuthentication } from 'Src/hooks/use-authentication/useAuthentication';
import { t } from 'Src/i18n';
import { Breadcrumb } from 'Components/frameworks/breadcrumb/Breadcrumb';
import { Hero } from 'Components/widgets/hero/Hero';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Disconnection } from 'Components/widgets/disconnection/Disconnection';
import { useAppContext } from 'Context/ReactAppContext';
type Props = {
  bannerIconName?: IconName;
  bannerImageSrc?: string;
  breadcrumbItems: Array<{
    dataTestId?: string;
    label: string;
    onClick?: () => void;
    url?: string;
  }>;
  children: ReactNode;
  id?: string;
  meta: {
    title: string;
  };
};
export const AccountView = ({
  bannerIconName,
  bannerImageSrc,
  breadcrumbItems = [],
  children,
  id,
  meta = { title: '' },
}: Props) => {
  const { isQuickLogged } = useAuthentication();
  const { isComingFromMobileApp } = useAppContext();
  return (
    <Fragment>
      <Head>
        <title key="title">{meta.title}</title>
      </Head>
      {isComingFromMobileApp ? (
        <main className="py-5" role="main">
          {children}
        </main>
      ) : (
        <Fragment>
          <main className="mt-navigation relative py-30" id={id} role="main">
            {(bannerIconName || bannerImageSrc) && (
              <div
                className={classnames(
                  'absolute top-0 -z-1 flex w-full items-end overflow-hidden text-end text-8 leading-0 text-grey sm:text-18',
                  {
                    'h-220 bg-pearl': !bannerImageSrc,
                    'h-260 bg-white': bannerImageSrc,
                  },
                )}
              >
                {bannerImageSrc && (
                  <div className="absolute inset-0 flex items-center overflow-hidden opacity-25">
                    <Hero className="flex-1" url={bannerImageSrc} />
                  </div>
                )}
                {bannerIconName && (
                  <SiteContainer>
                    <Icon
                      className="-mb-5 text-lightGrey sm:-mb-15"
                      name={bannerIconName}
                      width="10em"
                    />
                  </SiteContainer>
                )}
              </div>
            )}
            <SiteContainer className="mb-30 hidden md:block">
              <Breadcrumb
                items={[
                  {
                    dataTestId: 'CTA-my-member-space',
                    label: isQuickLogged
                      ? t('account.authenticated.dashboard')
                      : t('customerAccount.home.pageTitle'),
                    url: urlPattern('account'),
                  },
                  ...breadcrumbItems,
                ]}
              />
            </SiteContainer>
            {children}
          </main>
          <Disconnection className="mb-40" />
        </Fragment>
      )}
    </Fragment>
  );
};
